// Copyright ©️ 2026 eVolve MEP, LLC

import type { TaskType } from '@evolvemep/api-ts';
import { Checkbox, Flex, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { ImageFromFile } from 'components/Mantine/FileUploads/ImageFromFile';
import { GridMenu } from 'components/Mantine/GridMenu';
import { isNotNil } from 'helpers/isNotNil';

type TaskTypeListItemProps = {
  task: TaskType;
  onTaskSelectToggle: (toggleTask: TaskType) => void;
  isSelected: boolean;
  onEditTaskType: (taskType: TaskType) => void;
  onDeleteTaskType: (taskType: TaskType) => void;
};

export const TaskTypeListItem = ({
  onTaskSelectToggle,
  isSelected,
  task,
  onEditTaskType,
  onDeleteTaskType,
}: TaskTypeListItemProps) => {
  const disabled = task.isAssignedToWorkCell;

  return (
    <Flex
      align="center"
      h={70}
      justify="space-between"
      mx="md"
      my="sm"
      onClick={!disabled ? () => onTaskSelectToggle(task) : undefined}
    >
      <Flex gap="sm" align="center" justify="left" flex={1}>
        <Checkbox disabled={disabled} checked={isSelected} />
        <Flex gap="sm" flex={1}>
          {isNotNil(task.image) && (
            <ImageFromFile
              fileId={task.image.fileId}
              alt={task.name}
              size={80}
              withContainer
              fallback={<EvolveIcon icon="WorkCellTask" color="gray" />}
            />
          )}
          <Stack
            fz="sm"
            gap="xs"
            w="100%"
            justify="center"
            pr="lg"
            style={{
              userSelect: 'none',
            }}
          >
            <Flex gap="sm" align="center">
              <Text lineClamp={1}>{task.name}</Text>
              {task.isAssignedToWorkCell && (
                <AgGridStyleTooltip label="Assigned to a work cell">
                  <EvolveIcon icon="Pin" color="primary" size="sm" />
                </AgGridStyleTooltip>
              )}
            </Flex>
            {task.description && (
              <Text size="sm" c="dimmed" lineClamp={2}>
                {task.description}
              </Text>
            )}
          </Stack>
        </Flex>
      </Flex>
      <Flex align="center">
        <AttachmentsPopover taskTypeId={task.taskTypeId} numberOfAttachments={task.attachmentCount} />
        <GridMenu
          options={['Edit', 'Delete']}
          onMenuItemSelected={(option) => {
            if (option === 'Edit') onEditTaskType(task);
            else onDeleteTaskType(task);
          }}
        />
      </Flex>
    </Flex>
  );
};
